import Accordion from '@/common/components/Accordion'
import Box from '@/common/components/Box'
import Button from '@/common/components/Button'
import { ErrorMessage } from '@/common/components/ErrorMessage'
import { ErrorMessageWrapper } from '@/common/components/ErrorMessageWrapper'
import GoBackButton from '@/common/components/GoBackButton'
import { ImageCarousel } from '@/common/components/ImageCarousel'
import { ModalMessageSimple } from '@/common/components/ModalMessageSimple'
import SelectController from '@/common/components/SelectController'
import TextArea from '@/common/components/TextArea'
import useDebounce from '@/common/hooks/useDebounce'
import { useGoBack } from '@/common/hooks/useGoBack'
import useUserPermissions from '@/common/hooks/useUserPermissions'
import { Mapper } from '@/common/services/mapper'
import { Option } from '@/common/types/option'
import { FormTransformers } from '@/common/utils/formTransformers'
import { useEquipments } from '@/features/equipments/hooks/useEquipments'
import { EquipmentsTransformer } from '@/features/equipments/services/equipmentTransformer'
import { MaintenanceSolicitationObjectives } from '@/features/maintenanceSolicitations/components/MaintenanceSolicitationObjectives'
import { ModalAddMaintenanceObjective } from '@/features/maintenanceSolicitations/components/ModalAddMaintenanceObjective'
import {
  solicitationKindOptions,
  solicitationKindTranslation,
} from '@/features/maintenanceSolicitations/constants/solicitationKind.constant'
import { useMaintenanceSolicitation } from '@/features/maintenanceSolicitations/hooks/useMaintenanceSolicitation'
import { useUpdateMaintenanceSolicitation } from '@/features/maintenanceSolicitations/hooks/useUpdateMaintenanceSolicitation'
import {
  maintenanceSolicitationCreateSchema,
  maintenanceSolicitationObjectiveSchema,
  MaintenanceSolicitationObjectiveValidationSchema,
  MaintenanceSolicitationValidationSchema,
} from '@/features/maintenanceSolicitations/schemas/create.schema'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { ChevronsRightIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { FormProvider, SubmitHandler, useFieldArray, useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'

const Update = () => {
  const { id } = useParams()
  const goBack = useGoBack()
  const queryClient = useQueryClient()

  const [messages, setMessages] = useState<string[] | boolean>(false)

  const [searchEquipment, setSearchEquipment] = useDebounce('')

  const { data: equipmentOptions } = useEquipments({
    filters: { companyId: id, search: searchEquipment },
    mapper: Mapper.mapWithTransformer({ transformer: EquipmentsTransformer.optionsTransformer() }),
  })

  const { hasUserPermission, isUserPermissionsPending } = useUserPermissions()

  const userHasPermissionToUpdateMaintenanceSolicitation =
    isUserPermissionsPending || hasUserPermission('Client::Maintenance::Solicitations::Update')

  const { data: maintenanceSolicitation, isPending: maintenanceSolicitationIsPending } = useMaintenanceSolicitation(
    id || '',
  )

  const formMethods = useForm<MaintenanceSolicitationValidationSchema>({
    resolver: zodResolver(maintenanceSolicitationCreateSchema),
  })

  const {
    control,
    register,
    handleSubmit,
    setError,
    watch,
    reset,
    formState: { errors },
  } = formMethods

  const {
    fields: objectivesFields,
    append: appendObjective,
    remove: removeObjective,
  } = useFieldArray({
    control,
    name: 'objectives',
  })

  const { mutate: updateMaintenanceSolicitation, isPending: updateMaintenanceSolicitationIsPending } =
    useUpdateMaintenanceSolicitation({
      id: id || '',
      maintenanceSolicitation: maintenanceSolicitation,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['maintenanceSolicitation', id] })
        goBack({ fallback: `/maintenance-solicitations/${id}` })
      },
      onError: FormTransformers.errorsTransformer({ setError, setMessages }),
    })

  const onSubmit: SubmitHandler<MaintenanceSolicitationValidationSchema> = (data) => updateMaintenanceSolicitation(data)

  useEffect(() => {
    if (maintenanceSolicitationIsPending || !maintenanceSolicitation) return

    reset({
      equipment_id: maintenanceSolicitation.equipment
        ? {
            label: maintenanceSolicitation.equipment.field_for_display_value || '',
            value: maintenanceSolicitation.equipment.id,
          }
        : undefined,
      kind: maintenanceSolicitation.kind
        ? {
            label:
              solicitationKindTranslation[maintenanceSolicitation.kind as keyof typeof solicitationKindTranslation] ||
              maintenanceSolicitation.kind,
            value: maintenanceSolicitation.kind,
          }
        : undefined,
      observations: maintenanceSolicitation.observations || '',
      objectives: maintenanceSolicitation.objectives?.map((objective) => ({
        id: objective.id,
        system_id: { label: objective.system.display_name, value: objective.system.id },
        part_id: { label: objective.part.display_name, value: objective.part.id },
        action_id: { label: objective.action.display_name, value: objective.action.id },
      })),
    })
  }, [maintenanceSolicitationIsPending])

  return (
    <ErrorMessageWrapper
      className="h-[calc(100vh-128px)]"
      statusCode={userHasPermissionToUpdateMaintenanceSolicitation ? 200 : 403}
    >
      <FormProvider {...formMethods}>
        <form onSubmit={handleSubmit(onSubmit)} autoComplete="false" className="flex flex-col gap-y-6">
          <div className="flex justify-between items-center">
            <div className="flex gap-2 items-center">
              <GoBackButton fallback={`/maintenance-solicitations/${id}`} searchLastRouteFromFallback />

              <p>Solicitações</p>

              <ChevronsRightIcon className="text-white-400" />

              <h2>{maintenanceSolicitation?.id}</h2>
            </div>

            <div className="flex gap-4">
              <Button variant="outline-error" onClick={() => goBack({ fallback: `/maintenance-solicitations/${id}` })}>
                Cancelar
              </Button>

              <Button
                type="submit"
                name="update_maintenance_solicitation"
                className="shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]"
                disabled={updateMaintenanceSolicitationIsPending}
                isLoading={updateMaintenanceSolicitationIsPending}
              >
                Salvar
              </Button>
            </div>
          </div>

          {maintenanceSolicitationIsPending ? (
            <Box>Carregando...</Box>
          ) : (
            <Box className="max-h-[calc(100vh-128px)] flex flex-col gap-4 overflow-y-auto">
              {maintenanceSolicitation?.photos && maintenanceSolicitation.photos.length > 0 && (
                <ImageCarousel
                  files={maintenanceSolicitation.photos.map((photo) => ({
                    id: photo.url,
                    file: photo.url,
                  }))}
                  showRemoveButton={false}
                  showNavigationButtons
                  showExpandButton
                />
              )}

              <div className="flex w-full gap-x-2">
                <SelectController
                  control={control}
                  name="equipment_id"
                  label="Equipamento"
                  className="w-full"
                  options={(equipmentOptions as Option[]) || []}
                  onInputChange={setSearchEquipment}
                  isDisabled
                  errorMessage={errors.equipment_id?.message}
                />

                <SelectController
                  control={control}
                  name="kind"
                  label="Tipo de solicitação"
                  className="w-full"
                  options={solicitationKindOptions || []}
                  isDisabled
                  errorMessage={errors.kind?.message}
                />
              </div>

              <TextArea
                label="Descritivo da solicitação"
                {...register('observations')}
                disabled
                errorMessage={errors.observations?.message}
              />

              <Accordion
                defaultOpen
                className="py-1"
                head={
                  <div className="flex w-full flex-col items-start gap-y-1">
                    <p>
                      Objetos de manutenção <span className="text-danger-soft"> *</span>
                    </p>

                    {!!errors.objectives?.message && (
                      <ErrorMessage name="objectives" message={errors.objectives?.message} />
                    )}
                  </div>
                }
                body={
                  <div className="grid grid-cols-1 md:grid-cols-2 items-stretch justify-stretch gap-2">
                    <ModalAddMaintenanceObjective<MaintenanceSolicitationObjectiveValidationSchema> 
                      validationSchema={maintenanceSolicitationObjectiveSchema}
                      objectivesFields={objectivesFields}
                      appendObjective={appendObjective}
                      equipmentId={watch('equipment_id')?.value}
                      disabled={updateMaintenanceSolicitationIsPending}
                    />

                    <MaintenanceSolicitationObjectives
                      objectivesFields={objectivesFields}
                      removeObjective={removeObjective}
                    />
                  </div>
                }
              />
            </Box>
          )}

          {messages && <ModalMessageSimple messages={messages} setMessages={setMessages} isError />}
        </form>
      </FormProvider>
    </ErrorMessageWrapper>
  )
}

export default Update
